import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Grid,
  Icon,
  List,
  ListIcon,
  ListItem,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import {
  MdArrowBack,
  MdCheckCircle,
  MdExpandMore,
  MdInfoOutline,
} from "react-icons/md";
import { Redirect, useHistory, useParams } from "react-router-dom";
import Card from "components/card/Card";
import { useOptimizeAccess } from "hooks/useOptimizeAccess";
import { OptimizeCardDef, OptimizeGame, getCard } from "./data";
import { useOptimizeStore } from "./store";
import GameAvatar from "./components/GameAvatar";
import CardPreview from "./components/preview/CardPreview";
import { TM } from "./components/preview/primitives";
import GameSetup from "./components/GameSetup";
import { OptimizationKey, OptimizeStateEntry } from "./api";
import {
  useApp,
  useCardState,
  useOptimizeGames,
  useOptimizeLive,
} from "./liveStore";
import OptimizeStatus, { useOptimizeReady } from "./components/OptimizeStatus";

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// badge from the backend /state entries for this card+game, falling back to the
// /apps event prefill — per Günay, a prefilled event reads as "Active" to the
// partner, not as an internal "on file" state
const statusBadge = (
  entries: OptimizeStateEntry[] | undefined,
  onFile: boolean
) => {
  if (!entries || entries.length === 0)
    return onFile ? { label: "Active", colorScheme: "green" } : null;
  if (entries.some((entry) => entry.status === "enabled"))
    return { label: "Active", colorScheme: "green" };
  if (entries.some((entry) => entry.status === "paused"))
    return { label: "Paused", colorScheme: "orange" };
  // set up but never switched on — Günay almost missed this step, so the badge
  // says what's left to do rather than what's done
  return { label: "Needs activation", colorScheme: "red" };
};

// the app-replica previews floated loose on the card's white; a screen-colored,
// bordered surface reads as "this is the phone screen" (Günay's feedback). The
// fixed light colors are the replica's own — the previews don't have dark
// variants, in either color mode.
const PreviewFrame: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    bg={TM.bg}
    border="1px solid"
    borderColor="blackAlpha.200"
    borderRadius="20px"
    p="16px"
  >
    {children}
  </Box>
);

// ---------------------------------------------------------------- titles list

const TitleRow: React.FC<{
  game: OptimizeGame;
  card: OptimizeCardDef;
  selected: boolean;
  onSelect: () => void;
}> = ({ game, card, selected, onSelect }) => {
  const entries = useCardState(card.id, game.id);
  const app = useApp(game.id);
  const badge = statusBadge(
    entries,
    Boolean(app?.events?.[card.id as OptimizationKey])
  );
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const selectedBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.50");

  return (
    <Flex
      p="10px 12px"
      borderRadius="12px"
      bg={selected ? selectedBg : "transparent"}
      _hover={{ bg: selected ? selectedBg : hoverBg }}
      cursor="pointer"
      align="center"
      onClick={onSelect}
      transition="all 0.15s ease"
    >
      <GameAvatar game={game} size={40} />
      <Box ml="12px" flex="1">
        <Text color={textColor} fontWeight="600" fontSize="sm">
          {game.name}
        </Text>
        <Text color={subColor} fontSize="xs">
          {game.platform}
        </Text>
      </Box>
      {badge && (
        <Badge colorScheme={badge.colorScheme} borderRadius="full" px="8px">
          {badge.label}
        </Badge>
      )}
      <Icon
        as={MdExpandMore}
        color={subColor}
        w="20px"
        h="20px"
        ml="8px"
        transform={selected ? "rotate(180deg)" : undefined}
        transition="transform 0.2s ease"
      />
    </Flex>
  );
};

// ---------------------------------------------------------------- main page

export default function OptimizeDetail() {
  const { cardId } = useParams<{ cardId: string }>();
  const history = useHistory();
  const { isLoaded, hasOptimizeAccess } = useOptimizeAccess();
  const card = getCard(cardId);
  const markedGameId = useOptimizeStore((s) => s.markedGameId);
  const initLive = useOptimizeLive((s) => s.init);
  const ready = useOptimizeReady();
  const sourceGames = useOptimizeGames();

  const [selectedGameId, setSelectedGameId] = useState<string | null>(null);

  // repeated here because direct navigation can land on a detail page first
  useEffect(() => {
    initLive();
  }, [initLive]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const selectedBorder = useColorModeValue("brand.400", "brand.300");
  const iconBg = useColorModeValue(
    `${card?.accent ?? "gray"}.50`,
    "whiteAlpha.100"
  );
  const iconColor = useColorModeValue(
    `${card?.accent ?? "gray"}.500`,
    `${card?.accent ?? "gray"}.300`
  );

  // random order per visit; kept stable while picking so the inline setup
  // panel opens in place instead of the row jumping to the top
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const orderedGames = useMemo(() => shuffle(sourceGames), [sourceGames]);
  const selectedGame =
    sourceGames.find((g) => g.id === selectedGameId) ?? null;

  if (isLoaded && !hasOptimizeAccess) return <Redirect to="/ua" />;
  if (!isLoaded) return null;
  if (!card) return <Redirect to="/optimize" />;

  // Günay's layout for the two event-integration cards: the preview sits
  // beside the Benefits inside the description card — narrower, so the text
  // gets the room — and the Requirements move down beside the titles list,
  // where the preview used to be.
  const swapLayout =
    card.id === "recurring_event_structure" || card.id === "rewarded_ad_events";

  const previewGame =
    selectedGame ??
    sourceGames.find((g) => g.id === markedGameId) ??
    orderedGames[0] ??
    null;

  const requirementsList = card.requirements && (
    <List spacing={3}>
      {card.requirements.items.map((item, index) => (
        <ListItem
          key={index}
          color={textColor}
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="400"
          lineHeight="1.5"
        >
          <ListIcon
            as={MdInfoOutline}
            color="blue.400"
            w="22px"
            h="22px"
            mr="10px"
          />
          {item}
        </ListItem>
      ))}
    </List>
  );

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Button
        leftIcon={<Icon as={MdArrowBack} />}
        variant="ghost"
        size="sm"
        mb="16px"
        onClick={() => history.push("/optimize")}
      >
        Back to Optimize
      </Button>

      <Flex align="center" mb="24px">
        <Flex
          w="56px"
          h="56px"
          borderRadius="16px"
          bg={iconBg}
          align="center"
          justify="center"
          mr="16px"
        >
          <Icon as={card.icon} w="30px" h="30px" color={iconColor} />
        </Flex>
        <Box>
          <Text color={subColor} fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="0.5px">
            {card.section === "challenge" ? "Bonus Challenge" : "Integration"}
          </Text>
          <Text color={textColor} fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" lineHeight="1.2">
            {card.title}
          </Text>
          <Text color={textColor} fontSize={{ base: "md", md: "lg" }} fontWeight="600" mt="2px">
            {card.tagline}
          </Text>
        </Box>
      </Flex>

      {/* the copy comes first and full width: it is what partners actually
          need to read before they pick a title */}
      <Card p={{ base: "24px", md: "32px" }} mb="20px">
        {card.description.map((paragraph, index) => (
          <Text
            key={index}
            color={textColor}
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="400"
            lineHeight="1.65"
            mb="14px"
          >
            {paragraph}
          </Text>
        ))}
        {card.notice && (
          <Text
            color="red.500"
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="500"
            lineHeight="1.65"
            mb="14px"
          >
            {card.notice}
          </Text>
        )}

        <Grid
          templateColumns={{
            base: "1fr",
            md: swapLayout
              ? "1.5fr 1fr"
              : card.requirements
              ? "1fr 1fr"
              : "1fr",
          }}
          gap={{ base: "24px", md: "32px" }}
          mt="24px"
        >
          <Box>
            <Text
              color={textColor}
              fontWeight="bold"
              fontSize={{ base: "lg", md: "xl" }}
              mb="12px"
            >
              Benefits
            </Text>
            <List spacing={3}>
              {card.benefits.map((benefit, index) => (
                <ListItem
                  key={index}
                  color={textColor}
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="400"
                  lineHeight="1.5"
                >
                  <ListIcon
                    as={MdCheckCircle}
                    color="green.500"
                    w="22px"
                    h="22px"
                    mr="10px"
                  />
                  {benefit}
                </ListItem>
              ))}
            </List>
          </Box>

          {swapLayout ? (
            <Box>
              <Text
                color={textColor}
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl" }}
                mb="12px"
              >
                How it looks in the app
              </Text>
              {/* capped: full column width read as stretched (Günay). No
                  PreviewFrame here — beside the Benefits the grey surface
                  read as clutter, so these two cards show the app card bare */}
              <Box maxW="430px">
                <CardPreview cardId={card.id} game={previewGame} />
              </Box>
            </Box>
          ) : (
            card.requirements && (
              <Box>
                <Text
                  color={textColor}
                  fontWeight="bold"
                  fontSize={{ base: "lg", md: "xl" }}
                  mb="12px"
                >
                  {card.requirements.title}
                </Text>
                {requirementsList}
              </Box>
            )
          )}
        </Grid>
      </Card>

      <Grid
        templateColumns={{ base: "1fr", lg: "1fr 1.1fr" }}
        gap="20px"
        alignItems="start"
      >
        {/* titles + setup + activation */}
        <Box>
          <Card p="24px">
            <Text color={textColor} fontWeight="bold" fontSize="lg" mb="4px">
              Your titles
            </Text>
            <Text color={subColor} fontSize="sm" mb="14px">
              Pick a title to set it up right where you click.
            </Text>

            {!ready && <OptimizeStatus />}
            {/* The list scrolls inside the card on desktop instead of growing
                the page: the admin layout wrapper has overflow:auto, which
                kills position:sticky for everything inside it, so keeping the
                preview in view means keeping the page itself short. */}
            <Flex
              direction="column"
              gap="6px"
              mb="8px"
              maxH={{ lg: "calc(100vh - 320px)" }}
              overflowY={{ lg: "auto" }}
              pr={{ lg: "6px" }}
            >
              {orderedGames.map((game) => {
                const selected = selectedGameId === game.id;
                return (
                  <Box
                    key={game.id}
                    borderRadius="14px"
                    border="2px solid"
                    borderColor={selected ? selectedBorder : "transparent"}
                    transition="border-color 0.15s ease"
                  >
                    <TitleRow
                      game={game}
                      card={card}
                      selected={selected}
                      onSelect={() =>
                        setSelectedGameId(selected ? null : game.id)
                      }
                    />
                    <Collapse in={selected} animateOpacity unmountOnExit>
                      <Box px="12px" pb="14px">
                        <Divider mb="16px" />
                        <GameSetup card={card} game={game} />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Flex>
          </Card>
        </Box>

        {swapLayout ? (
          // the preview already sits up beside the Benefits; the Requirements
          // take its old slot next to the titles list
          card.requirements && (
            <Card p="24px">
              <Text color={textColor} fontWeight="bold" fontSize="lg" mb="12px">
                {card.requirements.title}
              </Text>
              {requirementsList}
            </Card>
          )
        ) : (
          /* The preview sits last: nice to have, not the point of the page.
             Sticky on desktop so it stays in view while scrolling a long title
             list (single-column mobile puts it below the list, where sticking
             has nothing to scroll against). */
          <Card
            p="24px"
            position={{ base: "static", lg: "sticky" }}
            top="100px"
          >
            <Text color={textColor} fontWeight="bold" fontSize="lg" mb="10px">
              How it looks in the app
            </Text>
            <PreviewFrame>
              <CardPreview cardId={card.id} game={previewGame} detail />
            </PreviewFrame>
          </Card>
        )}
      </Grid>
    </Box>
  );
}
